import { BreakpointName, breakpointNames } from '../../../../src/js/breakpoints.ts';
import { Expander } from '../../../../src/js/components/expander/expander.ts';

interface MapRegion {
  id: string;
  latlng: google.maps.LatLng;
  zoom: number;
}

interface Location {
  locationId: string; // a unique ID for each location
  regionId: string;   // a unique ID for the 'region' (Country) category for the location
  name: string;
  streetAddress: string;
  addressLocality: string;
  addressRegion: string;
  addressPostalCode: string;
  latitude: number;
  longitude: number;
  placeId: string;
  telephone: string;
}
type MapLocation = Location & { marker: google.maps.marker.AdvancedMarkerElement };

/**
 * Creates a control for the 'Our Locations' section where a list of locations are shown grouped by their region (mostly
 * by country) and a Google Map at the top showing markers for each of the locations in the list below. Clicking on a
 * region 'tab' filters the markers on the map to only the locations in that region. Clicking an individual location
 * from the list opens an InfoWindow on the map for the location's corresponding marker.
 */
export class LocationsMap {
  private tabs: {
    isSelected: boolean,
    elTrigger: HTMLElement,
    elTargets: HTMLElement[],
    //elImageTarget: HTMLElement | null,
    expanders: Expander[],
    region: MapRegion;
  }[] = [];
  private map: google.maps.Map | undefined;
  private mapLocations: MapLocation[] = [];
  private infoWindow: google.maps.InfoWindow | undefined;

  public constructor(elMapContainer: HTMLElement, map: google.maps.Map, locations: Location[]) {
    this.init(elMapContainer, map, locations);
  }

  /**
   *
   */
  private async init(elMapContainer: HTMLElement, map: google.maps.Map, locations: Location[]): Promise<void> {
    // create the google map
    const { infoWindow: infoWindow, mapLocations } = await this.createGMapElements(map, locations);
    this.map = map;
    this.infoWindow = infoWindow;
    this.mapLocations = mapLocations;

    // bind click event for the markers
    for (const { marker } of mapLocations) {
      marker.addListener('click', () => {
        infoWindow.close();
        infoWindow.setContent(marker.title);
        infoWindow.open(marker.map, marker);
      });
    }

    this.setMapCountryMarkers();

    const elMapTabs = elMapContainer.querySelector('.locs-map__tabs');
    if (!(elMapTabs instanceof HTMLElement)) {
      throw new Error('Could not identify region tabs.');
    }

    // identify tab related elements and properties
    this.tabs = [...elMapContainer.querySelectorAll('.locs-map__trigger')]
      .filter(elTrigger => elTrigger instanceof HTMLElement)
      .map((elTrigger) => {
        // identify the tab's corresponding region data
        const region: MapRegion = {
          id: elTrigger.dataset['regionId'] ?? '',
          latlng: new google.maps.LatLng(Number(elTrigger.dataset['regionLat']) || 0, Number(elTrigger.dataset['regionLng']) || 0),
          zoom: Number(elTrigger.dataset['regionZoom']) || 0,
        };

        // identify target elements (ignore any not found in document)
        const targetIds = (elTrigger.getAttribute('aria-controls') || '').split(' ').filter(targetId => targetId.trim());
        const elTargets = targetIds.map(targetId => document.getElementById(targetId)).filter(el => el !== null);
        //const elImageTarget = document.getElementById(elTrigger.dataset['imageTarget'] ?? '');

        // create Expander instances
        const elExpanders = elTargets
          .flatMap(elTarget => [...elTarget.querySelectorAll('.expander--locations')])
          .filter(elTarget => elTarget instanceof HTMLElement);
        const expanders: Expander[] = [];
        for (const elExpander of elExpanders) {
          const sizes = (elExpander.dataset['expanderSizes']?.split(' ').filter(size => size) ?? [])
            .filter((size): size is BreakpointName => breakpointNames.includes(size as BreakpointName));
          expanders.push(new Expander(elExpander, sizes));
        }

        return {
          isSelected: false,
          region,
          elTrigger,
          elTargets,
          //elImageTarget,
          expanders,
        };
      });

    // [click] tab/region
    for (const [tabIndex, { elTrigger }] of this.tabs.entries()) {
      elTrigger.addEventListener('click', (ev) => {
        ev.preventDefault();
        // determine if clicking this tab should expand location lists (expand lists when a tab has only one target)
        const expandLocationLists = this.tabs.find(tab => tab.elTrigger === ev.target)?.elTargets.length === 1;
        this.setActiveTab(tabIndex, expandLocationLists);
      });
    }

    // [keydown] tab/region
    elMapTabs.addEventListener('keydown', (ev) => {
      // if event NOT fired from trigger
      if (!(ev.target instanceof HTMLElement) || !this.tabs.map(({ elTrigger }) => elTrigger).includes(ev.target)) {
        return;
      }

      // determine if clicking this tab should expand location lists (expand lists when a tab has only one target)
      const expandLocationLists = (this.tabs.find(tab => tab.elTrigger === ev.target)?.elTargets.length ?? 0) > 1;

      let nextTabIndex;
      if (ev.key === 'ArrowUp' || (ev.ctrlKey && ev.key === 'PageUp')) {            // [ArrowUp] OR CTRL + PageUp     -> go to previous tab
        nextTabIndex = (this.tabs.findIndex(({ isSelected }) => isSelected) ?? 0) - 1;
      } else if (ev.key === 'ArrowDown' || (ev.ctrlKey && ev.key === 'PageDown')) { // [ArrowDown] OR CTRL + PageDown -> go to next tab
        nextTabIndex = (this.tabs.findIndex(({ isSelected }) => isSelected) ?? 0) + 1;
      } else if (ev.key === 'Home') {                                               // [Home]                         -> go to first tab
        nextTabIndex = 0;
      } else if (ev.key === 'End') {                                                // [End]                          -> go to last tab
        nextTabIndex = this.tabs.length - 1;
      }

      if (nextTabIndex !== undefined) {
        this.setActiveTab(nextTabIndex, expandLocationLists);
        ev.preventDefault();
      }
    });

    this.setActiveTab(0);
  }

  /**
   * Create the google Map, InfoWindow, and location Marker elements.
   */
  private async createGMapElements(map: google.maps.Map, locations: Location[]): Promise<{ infoWindow: google.maps.InfoWindow, mapLocations: MapLocation[] }> {
    const { InfoWindow } = await (google.maps.importLibrary('maps') as Promise<google.maps.MapsLibrary>);
    const { AdvancedMarkerElement, PinElement } = await (google.maps.importLibrary('marker') as Promise<google.maps.MarkerLibrary>);

    // does the infoWindow not have to be referenced to the map somehow?
    const infoWindow = new InfoWindow();

    const mapLocations: MapLocation[] = locations.map(location => {
      const pin = new PinElement({
        scale: 1.5,
        background: '#f46c15',
        borderColor: '#f46c15',
        glyphColor: 'orange',
      });

      const marker = new AdvancedMarkerElement({
        position: new google.maps.LatLng({ lat: location.latitude, lng: location.longitude }),
        map,
        title: `
          <b>${location.name}</b><br>
          <i>${location.streetAddress}, ${location.addressLocality}, ${location.addressRegion} ${location.addressPostalCode}</i><br><br>
          <p><b>Rental Sales</b> - 1.888.508.3226<br><b>Rental Support</b> - 1.833.661.LINK<br><b>Office</b> - ${location.telephone}</p>
          <p><b>Email</b> - <a href= "mailto: sales@superheat.com">sales@superheat.com</a></p>
          <a href="https://www.google.com/maps/place/?q=place_id:${location.placeId}" target="_blank">View on Google Maps</a>
        `,
        content: pin.element,
        gmpClickable: true,
      });

      return { ...location, marker };
    });

    return { infoWindow, mapLocations };
  }

  /**
   * Filter the displayed map markers and only show those that correspond to the provided region ID
   * - if no region ID is provided, show all locations
   */
  private setMapCountryMarkers(regionId?: string): void {
    let hiddenLocations: MapLocation[] = [];
    let visibleLocations = this.mapLocations;

    // if a region ID has been provided, split the map locations into those that match the region (show) and those that
    // don't (hide)
    if (regionId && regionId !== 'all') {
      ({ visible: visibleLocations, hidden: hiddenLocations } = this.mapLocations.reduce((splitLocations, mapLocation) => {
        mapLocation.regionId === regionId
          ? splitLocations.visible.push(mapLocation)
          : splitLocations.hidden.push(mapLocation);

        return splitLocations;
      }, { hidden: <MapLocation[]>[], visible: <MapLocation[]>[] }));
    }

    for (const location of hiddenLocations) {
      location.marker.map = null;
    }
    for (const location of visibleLocations) {
      location.marker.map = this.map;
    }
  }

  /**
   * @todo move this functionality to a public method 'setActiveTab()' and bind tab click events outside of the class so
   * the class isn't dependent on any particular HTML structure.
   * - unsure how to handle 'expandLocationLists' when converted to public
   */
  private setActiveTab(tabIndex: number, expandLocationLists = false): void {
    // if tabIndex is out of range, use modulus with # tabs as index
    // tabIndex = tabIndex % this.tabs.length;
    // https://web.archive.org/web/20090717035140if_/javascript.about.com/od/problemsolving/a/modulobug.htm
    const n = this.tabs.length;
    if (n === 0) {
      return;
    }

    tabIndex = (((tabIndex % n) + n) % n);
    const selTab = this.tabs[tabIndex];
    if (!selTab) {
      return;
    }

    // update the map markers / map position / map zoom for the selected tab
    const elTab = selTab.elTrigger;
    const regionId = elTab.dataset['regionId'];
    this.setMapCountryMarkers(regionId);
    this.map?.panTo(selTab.region.latlng);
    this.map?.setZoom(selTab.region.zoom);
    this.infoWindow?.close();

    // expand location list(s) for the selected tab, or collapse all location lists
    if (expandLocationLists) {
      selTab?.expanders.forEach(expander => expander.expand()); // expand
    } else {
      this.tabs.flatMap(tab => tab.expanders).forEach(expander => expander.collapse()); // collapse
    }

    const elUniqueTargets = new Set(this.tabs.map(({ elTargets }) => elTargets).flat());
    const elActiveTargets = selTab?.elTargets ?? [];
    const elInactiveTargets = [...elUniqueTargets].filter(target => !elActiveTargets.includes(target));

    // update tabs and images
    this.tabs.forEach((tab, i) => {
      // select/unselect triggers and targets
      tab.isSelected = i === tabIndex;

      // if (tab.isSelected) {
      //   tab.elImageTarget?.removeAttribute('hidden');
      // } else {
      //   tab.elImageTarget?.setAttribute('hidden', '');
      // }

      tab.elTrigger.setAttribute('aria-expanded', tab.isSelected.toString());
    });

    // update targets
    elActiveTargets.forEach(target => target.removeAttribute('hidden'));
    elInactiveTargets.forEach(target => target.setAttribute('hidden', ''));

    // recalculate the alternating colour scheme of the countries, and the last module separator
    this.stripeVisibleTargets();
  }

  // public setActiveTab(tabId: string, expandLocationLists = false): void {
  //   const selTab = this.tabs.find(tab => tab.region.id === tabId);
  //   if (!selTab) {
  //     return;
  //   }

  //   // update the map markers / map position / map zoom for the selected tab
  //   const elTab = selTab.elTrigger;
  //   const regionId = elTab.dataset.regionId;
  //   this.setMapCountryMarkers(regionId);
  //   this.map?.panTo(selTab.region.latlng);
  //   this.map?.setZoom(selTab.region.zoom);

  //   // expand location list(s) for the selected tab, or collapse all location lists
  //   if (expandLocationLists) {
  //     selTab?.expanders.forEach(expander => expander.expand()); // expand
  //   } else {
  //     this.tabs.flatMap(tab => tab.expanders).forEach(expander => expander.collapse()); // collapse
  //   }

  //   const elUniqueTargets = new Set(this.tabs.map(({ elTargets }) => elTargets).flat());
  //   const elActiveTargets = selTab?.elTargets ?? [];
  //   const elInactiveTargets = [...elUniqueTargets].filter(target => !elActiveTargets.includes(target));

  //   // update tabs and images
  //   this.tabs.forEach((tab, i) => {
  //     // select/unselect triggers and targets
  //     //tab.isSelected = i === tabIndex;
  //     tab.isSelected = tab.region.id === tabId;

  //     // if (tab.isSelected) {
  //     //   tab.elImageTarget?.removeAttribute('hidden');
  //     // } else {
  //     //   tab.elImageTarget?.setAttribute('hidden', '');
  //     // }

  //     tab.elTrigger.setAttribute('aria-expanded', tab.isSelected.toString());
  //   });

  //   // update targets
  //   elActiveTargets.forEach(target => target.removeAttribute('hidden'));
  //   elInactiveTargets.forEach(target => target.setAttribute('hidden', ''));

  //   // recalculate the alternating colour scheme of the countries, and the last module separator
  //   this.stripeVisibleTargets();
  // }

  /**
   * Alternate/interleave/zebra-stripe color schemes of visible target modules
   */
  private stripeVisibleTargets(): void {
    const visibleTargets = (this.tabs.filter(tab => tab.isSelected) || []).flatMap(tab => tab.elTargets);
    for (const [i, visibleTarget] of visibleTargets.entries()) {
      // set odd modules to have the secondary color scheme
      const moduleStyles = ['module--primary', 'module--secondary'];
      if (i % 2) {
        moduleStyles.reverse();
      }

      visibleTarget.classList.remove(moduleStyles[1] ?? '');
      visibleTarget.classList.add(moduleStyles[0] ?? '');

      // set the last module to have a straight bottom separator
      const separatorStyles = ['chevron-down', 'straight'];
      if (i === visibleTargets.length - 1) {
        separatorStyles.reverse();
      }

      visibleTarget.classList.remove(`module--${separatorStyles[1]}`);
      visibleTarget.classList.add(`module--${separatorStyles[0]}`);

      const separator = visibleTarget.nextElementSibling;
      if (separator !== null) {
        separator.classList.remove(`separator--${separatorStyles[1]}`);
        separator.classList.add(`separator--${separatorStyles[0]}`);
      }
    }
  }

  /**
   * Open the infoWindow for the provided location's marker
   */
  public showLocationInfo(locationId: Location['locationId']): void {
    if (!this.infoWindow) {
      return;
    }

    // identify the marker for the provided location ID
    const location = this.mapLocations.find(mapLocation => mapLocation.locationId === locationId);
    if (!location) {
      return;
    }

    this.infoWindow.close();
    this.infoWindow.setContent(location.marker.title);
    this.infoWindow.open(location.marker.map, location.marker);
  }
}

(() => {
  const onLocationsMapReady = async () => {
    // identify list of locations from `.locs-list .locs-list__loc`
    const elLocations = Array.from(document.querySelectorAll('.locs-list .locs-list__loc')).filter(element => element instanceof HTMLElement);
    const locations: Location[] = elLocations.map(elLocation => {
      const elName              = elLocation.querySelector(':scope[itemscope] > [itemprop=\'name\']');
      const elStreetAddress     = elLocation.querySelector(':scope > [itemscope][itemprop=\'address\'] > [itemprop=\'streetAddress\'], :scope > [itemscope][itemprop=\'address\'] :not([itemscope]) [itemprop=\'streetAddress\']');
      const elAddressLocality   = elLocation.querySelector(':scope > [itemscope][itemprop=\'address\'] > [itemprop=\'addressLocality\'], :scope > [itemscope][itemprop=\'address\'] :not([itemscope]) [itemprop=\'addressLocality\']');
      const elAddressRegion     = elLocation.querySelector(':scope > [itemscope][itemprop=\'address\'] > [itemprop=\'addressRegion\'], :scope > [itemscope][itemprop=\'address\'] :not([itemscope]) [itemprop=\'addressRegion\']');
      const elAddressPostalCode = elLocation.querySelector(':scope > [itemscope][itemprop=\'address\'] > [itemprop=\'postalCode\'], :scope > [itemscope][itemprop=\'address\'] :not([itemscope]) [itemprop=\'postalCode\']');
      const elAddressLatitude   = elLocation.querySelector(':scope > [itemscope][itemprop=\'address\'] > [itemprop=\'latitude\'], :scope > [itemscope][itemprop=\'address\'] :not([itemscope]) [itemprop=\'latitude\']');
      const elAddressLongitude  = elLocation.querySelector(':scope > [itemscope][itemprop=\'address\'] > [itemprop=\'longitude\'], :scope > [itemscope][itemprop=\'address\'] :not([itemscope]) [itemprop=\'longitude\']');
      const elPlaceId           = elLocation.querySelector(':scope > [itemscope][itemprop=\'address\'] > [name=\'placeId\']');
      const elTelephone         = elLocation.querySelector(':scope > [itemscope][itemprop=\'address\'] > [itemprop=\'telephone\'], :scope > [itemscope][itemprop=\'address\'] :not([itemscope]) [itemprop=\'telephone\']');

      return {
        locationId: elLocation.dataset['locationId'] ?? '',
        regionId: elLocation.dataset['regionId'] ?? '',
        name: elName?.textContent ?? '',
        streetAddress: elStreetAddress?.textContent ?? '',
        addressLocality: elAddressLocality?.textContent ?? '',
        addressRegion: elAddressRegion?.textContent ?? '',
        addressPostalCode: elAddressPostalCode?.textContent ?? '',
        latitude: Number(elAddressLatitude?.getAttribute('content')) || 0,
        longitude: Number(elAddressLongitude?.getAttribute('content')) || 0,
        placeId: elPlaceId?.getAttribute('content') ?? '',
        telephone: elTelephone?.getAttribute('content') ?? '',
      };
    });

    // Create Locations Map functionality (Change countries, expand/collapse location lists)
    const elMap = document.querySelector('.locs-map');
    const elGMap = elMap?.querySelector('.locs-map__gmap');
    if (elMap instanceof HTMLElement && elGMap instanceof HTMLElement) {
      // create a google map instance
      const { Map } = await (google.maps.importLibrary('maps') as Promise<google.maps.MapsLibrary>);
      const map = new Map(elGMap, {
        zoom: 3,
        center: { lat: 38.54060682091429, lng: -35.607759972079755 },
        mapId: 'superheat-locations-map',
      });

      // create the map instance
      const locationsMap = new LocationsMap(elMap, map, locations);

      // [click] location - show location's corresponding map marker
      for (const elLocation of elLocations) {
        const elLink = elLocation.querySelector('.loc__link');
        if (!elLink) {
          continue;
        }

        const locationId = elLocation.dataset['locationId'];
        if (!locationId) {
          continue;
        }
        elLink.addEventListener('click', (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          locationsMap.showLocationInfo(locationId);
        });
      }
    }
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', onLocationsMapReady) : onLocationsMapReady();
})();
